import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import ModalComponent from '../components/ModalComponent';
import CreateUser from '../components/CreateUser';
import EditUser from '../components/EditUser';
import './AdminConsole.css';
import { Link } from 'react-router-dom';
import { getAdminData, deleteUser } from '../api/adminApi';
import { IoSearchOutline } from "react-icons/io5";
import { MdFilterListOff } from "react-icons/md";
import { IoIosAdd } from "react-icons/io";
import { FaUserEdit } from "react-icons/fa";
import Loading from '../components/Loading';
import { AiOutlineDelete } from "react-icons/ai";
import Swal from 'sweetalert2';

const AdminConsole = () => {
  const gridApiRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const title = 'O2E Digital Journey - Admin Console'; 
  const userMail = localStorage.getItem("userId");
  

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  const [rowData, setRowData] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Format admin data for grid display
  const formatAdminData = (data) => {
    return data.map((item) => ({
      id: item.id || '-',
      name: item.name || '-',
      emailId: item.emailId || '-',
      role: item.role || '-',
      status: item.status || '-',
      business: item.business || '-',
      accessCategory: item.accessCategory || '-',
    }));
  };

  // Fetch and update grid data
  const fetchAndUpdateGrid = async () => {
    try {
      setLoading(true);
      const data = await getAdminData();
      setRowData(formatAdminData(data));
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to fetch admin data');
    } finally {
      setLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    document.title = title;
    fetchAndUpdateGrid();
  }, [title]);
  
  // Refresh grid data
  const refreshGridData = () => fetchAndUpdateGrid();

  // Grid event handlers
  const onSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();
    setSelectedUser(selectedRows.length > 0 ? selectedRows[0] : null);
  };

  // Grid configuration
  const [columnDefs] = useState([
    { 
      headerCheckboxSelection: true, 
      checkboxSelection: true, 
      sortable: false, 
      filter: false, 
      width: 50,
      suppressSizeToFit: true 
    },
    { headerName: 'Name', field: 'name' },
    { headerName: 'Email ID', field: 'emailId'},
    { headerName: 'Role', field: 'role'},
    { headerName: 'Status', field: 'status' },
    { headerName: 'Business', field: 'business' },
    { headerName: 'Access Category', field: 'accessCategory'},
  ]);

  const defaultColDef = useMemo(() => ({
    filter: true,
    sortable: true,
    resizable: true,
  }), []);
  
  const filteredRowData = useMemo(() => {
    return rowData.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, rowData]);

  const onGridReady = (params) => {
    gridApiRef.current = params.api;
    setGridApi(params.api);
  };

  const clearAllFilters = () => {
    if (gridApi) {
      gridApi.setFilterModel(null);
      gridApi.onFilterChanged();
      setSearchTerm('');
    }
  };
  
  // Modal handlers
  const handleModals = {
    add: {
      close: () => setShowAddModal(false),
      show: () => setShowAddModal(true)
    },
    edit: {
      close: () => setShowEditModal(false),
      show: () => {
        if (selectedUser) {
          setShowEditModal(true);
        } else {
          toast.error('Please select a user to edit');
        }
      }
    }
  };
  // Handle user deletion
  const handleDelete = async () => {
    if (!selectedUser) {
      toast.error('Please select a user to delete');
      return;
    }

    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Confirm Delete',
      text: `Are you sure you want to delete "${selectedUser.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      await deleteUser(selectedUser.id, selectedUser.emailId);
      toast.success(`User "${selectedUser.name}" deleted successfully`);

      const data = await getAdminData();
      const formattedData = data.map((item) => ({
        id: item.id || '-',
        name: item.name || '-',
        emailId: item.emailId || '-',
        role: item.role || '-',
        status: item.status || '-',
        business: item.business || '-',
        accessCategory: item.accessCategory || '-',
      }));
      setRowData(formattedData);
      setSelectedUser(null);
    } catch (error) {
      toast.error('Failed to delete user');
      console.error('Delete error:', error);
    } finally {
      setLoading(false);
    }
  };

  if(loading) {
    return <Loading />
  }

  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick={true}
        pauseOnHover={true}
        draggable={true}
        closeButton={true}
        className="large-toast"
      />
      <section className='section-block'>
        <div className='page-header'>
          <Link to="/oppurtunity-list">
            <img src="./image/icons/back.svg" alt='Back to Page' />
          </Link>
          <h1>Admin Console</h1>
        </div>

        <div className="admin-console-table">
          <div className='table-action-header'>
            <div className='search-input'> 
              <input className='form-control' type='text' placeholder='search here' value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /> 
              <IoSearchOutline />
            </div>
            <div className='table-btn-actions'>
              <button onClick={clearAllFilters} className='btn btn-action-outline'><MdFilterListOff/> Clear</button>
              <button className='btn btn-action-outline' onClick={handleModals.add.show}><IoIosAdd/> Add User</button>
              <button className='btn btn-action-outline' onClick={handleModals.edit.show}><FaUserEdit/> Edit</button>
              <button className='btn btn-action-outline' onClick={handleDelete}><AiOutlineDelete/> Delete</button>
            </div>
          </div>

          <div className='ag-grid-table ag-theme-quartz' style={{ height: 400, width: '100%' }}>
            <AgGridReact
              className='ag-grid-height'
              rowData={filteredRowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={10}
              paginationPageSizeSelector={[10, 20, 50]}
              onGridReady={onGridReady}
              onSelectionChanged={onSelectionChanged}
              rowSelection="single"
              overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Loading...</span>'}
              overlayNoRowsTemplate={
                loading
                  ? '<span class="ag-overlay-loading-center">Loading...</span>'
                  : '<span class="ag-overlay-loading-center">No data available</span>'
              }
            />
          </div>
        </div>
      </section>

      {/* Add Modal */}
      <ModalComponent
        show={showAddModal}
        handleClose={handleModals.add.close}
        title="Add User Detail"
        bodyContent={<CreateUser 
          handleCloseAddModal={handleModals.add.close} 
          onCreateSuccess={refreshGridData}
          userMail={userMail}
        />}
        size="lg"
      />

      {/* Edit Modal */}
      <ModalComponent
        show={showEditModal}
        handleClose={handleModals.edit.close}
        title="Edit User Detail"
        bodyContent={<EditUser 
          user={selectedUser} 
          handleCloseEditModal={handleModals.edit.close} 
          userMail={userMail}
          onUpdateSuccess={refreshGridData}
        />}
        size="lg"
      />

    </>
  );
};

export default AdminConsole;
